import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  ABSENT_LINE,
  ASSESSMENT_LABEL,
  ReviewAssessment,
  ReviewOffer,
  VERDICT_LABEL,
  VERDICT_ORDER,
  disagreementCount,
  resultRows,
  reviewOffer,
  type AdversarialRun,
  type ReviewDisagreement,
} from '../lib/adversarialReview.ts'
import type { GuidedStep } from '../lib/guidedSteps.ts'
import { Button } from './Button.tsx'
import { ComparisonBoard } from './ComparisonColumns.tsx'
import { DisagreementNote } from './DisagreementNote.tsx'
import { FOCUS_RING, FOCUS_RING_ROW } from './focusRing.ts'
import { icons } from './icons.ts'
import { Tag } from './Tag.tsx'

/**
 * One adversarial review, from the press to the result (#541).
 *
 * The same modal the whole way: it opens when a run is started and shows how
 * far it has got, and turns into the result when it lands. Opened on a run
 * that is already finished, it first asks what to do with it -- read the
 * result, walk it on the conferência, or run another -- and grows into the
 * result when that is the choice. A run is minutes of
 * model work, so the wait is part of it -- the modal can be closed and opened
 * again at any point, and the run does not notice.
 *
 * The result is one table of the lines the review disputes and nothing else,
 * drawn as the conferência draws them: receita beside sistema, under the entry
 * and section they belong to. Where the conferência has its decision buttons,
 * this has what was answered about the review. Under the table is the analysis
 * of whichever line is chosen, and only that.
 *
 * Read-only by construction. Nothing here can answer the conferência: this is
 * a look at how we judged, not a place to judge the pharmacy.
 */
export interface AdversarialReviewModalProps {
  /** The latest run; `null` or `undefined` for the moment before a start lands. */
  readonly run: AdversarialRun | null | undefined
  /** A start is on its way to the server. */
  readonly starting: boolean
  /** Ours in reaching the server, as opposed to the run's own failure. */
  readonly error?: string | undefined
  /** Every step of the conferência, as the page built them. */
  readonly steps: readonly GuidedStep[]
  readonly onAssess: (disagreement: string, assessment: ReviewAssessment | null) => void
  /** Goes to a step on the conferência and closes this. */
  readonly onJump: (stepIndex: number, disagreement: ReviewDisagreement) => void
  /** Runs it again. Absent where it may not be, e.g. mid-analysis. */
  readonly onRun?: (() => void) | undefined
  /** Starts the guided tour. Absent when nothing disputed has a row to stand on. */
  readonly onWalk?: (() => void) | undefined
  readonly onClose: () => void
}

/** What the modal is showing of a run that can be opened. */
enum ReadyView {
  /** The three things to do with it, to choose from. */
  Options = 'options',
  Result = 'result',
}

export function AdversarialReviewModal({
  run,
  starting,
  error,
  steps,
  onAssess,
  onJump,
  onRun,
  onWalk,
  onClose,
}: AdversarialReviewModalProps) {
  const offer = starting ? ReviewOffer.Running : reviewOffer(run)
  const ready = offer === ReviewOffer.Ready && run !== null && run !== undefined
  /*
   * Opened on a finished run, it asks what to do with it. Opened to follow a
   * run -- or still open when one lands -- it goes straight to the result:
   * whoever watched the clock was waiting for exactly that.
   */
  const [view, setView] = useState(() =>
    offer === ReviewOffer.Ready ? ReadyView.Options : ReadyView.Result,
  )
  const wide = ready && view === ReadyView.Result

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Revisão adversarial"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        // One modal for both: it grows into the result rather than being
        // replaced by another, so the choice and what it opens read as one act.
        className={`rounded-base bg-background-raised border-stroke-weak flex max-h-[85vh] w-full flex-col gap-4 overflow-y-auto border p-4 transition-[max-width] duration-200 ${
          wide ? 'max-w-[72rem]' : 'max-w-[44rem]'
        }`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-tPSemi text-text-strong">Revisão adversarial</h2>
            {ready && (
              <span className="text-tSmall text-text-weak">
                {disagreementCount(run.disagreements.length)}
                {run.model === null ? '' : ` · ${run.model}`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {wide && (
              <Button
                variant="tertiary"
                tone="neutral"
                size="small"
                leadingIcon={icons.back}
                onClick={() => setView(ReadyView.Options)}
              >
                Opções
              </Button>
            )}
            {/* Closing mid-run does not stop the run: it is a workflow of its
                own, and the button in the header opens this again. */}
            <Button variant="tertiary" tone="neutral" size="small" onClick={onClose}>
              {offer === ReviewOffer.Running ? 'Fechar e deixar rodando' : 'Fechar'}
            </Button>
          </div>
        </div>

        {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

        {offer === ReviewOffer.Running && <Running run={run} />}

        {offer === ReviewOffer.Failed && (
          <Stopped onRun={onRun}>
            A revisão falhou: {run?.error ?? 'sem motivo registrado'}.
          </Stopped>
        )}

        {offer === ReviewOffer.Outdated && (
          // Not opened: its notes point at findings that have been rewritten.
          <Stopped onRun={onRun}>
            A conferência foi analisada de novo depois desta revisão, que ficou desatualizada.
          </Stopped>
        )}

        {offer === ReviewOffer.None && (
          <Stopped onRun={onRun}>Nenhuma revisão foi rodada nesta conferência.</Stopped>
        )}

        {ready && view === ReadyView.Options && (
          <Options
            run={run}
            onOpenResult={() => setView(ReadyView.Result)}
            onWalk={onWalk}
            onRun={onRun}
          />
        )}

        {ready && view === ReadyView.Result && (
          <AdversarialReviewResult run={run} steps={steps} onAssess={onAssess} onJump={onJump} />
        )}
      </div>
    </div>
  )
}

/**
 * What to do with a finished review: read it, walk it, or run another.
 *
 * Three large choices rather than a menu, each with a line saying what it
 * does -- one of them costs a model run and one takes over the page, and
 * neither should be a guess.
 */
function Options({
  run,
  onOpenResult,
  onWalk,
  onRun,
}: {
  run: AdversarialRun
  onOpenResult: () => void
  onWalk?: (() => void) | undefined
  onRun?: (() => void) | undefined
}) {
  const none = run.disagreements.length === 0
  return (
    <div className="flex flex-col gap-4 py-2">
      <p className="text-tP text-text-weak text-center">
        {none
          ? 'A última revisão não encontrou nada errado.'
          : `A última revisão encontrou ${disagreementCount(run.disagreements.length)}.`}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Option icon={icons.reviewResult} title="Abrir resultado" onClick={onOpenResult} primary>
          As linhas em disputa em uma tabela, com o parecer da revisão e a nossa avaliação.
        </Option>
        <Option icon={icons.reviewTour} title="Percorrer guiado" onClick={onWalk}>
          {onWalk === undefined
            ? 'Nada em disputa tem uma linha na conferência para apontar.'
            : 'Um balão em cada linha em disputa, na própria conferência, dizendo o que está errado.'}
        </Option>
        <Option icon={icons.refresh} title="Rodar de novo" onClick={onRun}>
          {onRun === undefined
            ? 'Aguarde a análise da conferência terminar.'
            : 'Uma nova revisão, do zero. Leva alguns minutos e a anterior fica guardada.'}
        </Option>
      </div>
    </div>
  )
}

/** One choice: an icon, what it is called, and a line on what it does. */
function Option({
  icon,
  title,
  children,
  onClick,
  primary = false,
}: {
  icon: IconDefinition
  title: string
  children: string
  /** Absent when the choice cannot be made; the line under it says why. */
  onClick?: (() => void) | undefined
  primary?: boolean
}) {
  return (
    <button
      type="button"
      disabled={onClick === undefined}
      data-review-option={title}
      className={`rounded-base flex flex-col items-center gap-2 border p-4 text-center transition-colors ${FOCUS_RING} ${
        onClick === undefined
          ? 'border-stroke-weak cursor-default opacity-60'
          : primary
            ? 'border-stroke-brand-strong hover:bg-fill-brand-weak cursor-pointer'
            : 'border-stroke-weak hover:bg-fill-weak cursor-pointer'
      }`}
      onClick={onClick}
    >
      <span className="bg-fill-brand-weak flex h-12 w-12 items-center justify-center rounded-full">
        <FontAwesomeIcon icon={icon} className="text-icon-brand text-lg" aria-hidden="true" />
      </span>
      <span className="text-tPSemi text-text-strong">{title}</span>
      <span className="text-tSmall text-text-weak">{children}</span>
    </button>
  )
}

/**
 * The wait, with the clock running and the two halves named.
 *
 * Elapsed rather than a percentage, as in `RunModal`: nothing knows how far
 * into a model call it is, and a bar that invents one is a lie. What is known
 * is which of the two calls is under way, so that is what is shown.
 */
function Running({ run }: { run: AdversarialRun | null | undefined }) {
  const since = run?.createdAt
  const [seconds, setSeconds] = useState(() => elapsed(since))

  useEffect(() => {
    setSeconds(elapsed(since))
    const tick = setInterval(() => setSeconds(elapsed(since)), 1000)
    return () => clearInterval(tick)
  }, [since])

  const read = run?.blindRead === true
  return (
    <div className="rounded-base border-stroke-weak bg-fill-weak/40 flex flex-col gap-2 border p-3">
      <p className="text-tP text-text-strong" role="status" aria-live="polite">
        Rodando… {clock(seconds)}
      </p>
      <ol className="text-tP flex flex-col gap-1">
        <Stage state={read ? 'done' : 'now'}>
          Lendo a receita de novo, sem ver a nossa leitura
        </Stage>
        <Stage state={read ? 'now' : 'next'}>
          Comparando com a leitura, o pareamento e os apontamentos
        </Stage>
      </ol>
      <p className="text-tSmall text-text-weak">
        Costuma levar de um a três minutos. Pode fechar esta janela e continuar usando a tela: o
        botão no cabeçalho abre de novo, e o resultado aparece aqui quando terminar.
      </p>
    </div>
  )
}

function Stage({ state, children }: { state: 'done' | 'now' | 'next'; children: string }) {
  const MARK = { done: '✓', now: '●', next: '○' } as const
  const TONE = {
    done: 'text-text-weak',
    now: 'text-text-strong font-semibold',
    next: 'text-text-weak',
  } as const
  return (
    <li className={`flex items-baseline gap-2 ${TONE[state]}`} data-stage={state}>
      <span aria-hidden="true">{MARK[state]}</span>
      {children}
    </li>
  )
}

/** Seconds since a run was created, by this browser's clock. Never negative. */
function elapsed(since: string | undefined): number {
  if (since === undefined) return 0
  const ms = Date.now() - Date.parse(since)
  return Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0
}

function clock(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`
  return `${String(Math.floor(seconds / 60))}min ${String(seconds % 60).padStart(2, '0')}s`
}

/** A run with nothing to open, and the one thing to do about it. */
function Stopped({ children, onRun }: { children: ReactNode; onRun?: (() => void) | undefined }) {
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="text-tP text-text-strong">{children}</p>
      <Button
        variant="primary"
        tone="brand"
        size="small"
        disabled={onRun === undefined}
        onClick={onRun}
      >
        Rodar revisão
      </Button>
    </div>
  )
}

/**
 * The result: one table of the lines the review disputes, and under it what
 * the review says about the chosen one.
 *
 * One line per disagreement and nothing else. The lines the review agreed
 * with are the conferência itself, one click away; putting them here would
 * make somebody read past what is right to find what is wrong.
 */
export function AdversarialReviewResult({
  run,
  steps,
  onAssess,
  onJump,
}: {
  run: AdversarialRun
  steps: readonly GuidedStep[]
  onAssess: (disagreement: string, assessment: ReviewAssessment | null) => void
  onJump: (stepIndex: number, disagreement: ReviewDisagreement) => void
}) {
  const rows = useMemo(() => resultRows(steps, run.disagreements), [steps, run.disagreements])
  const [chosen, setChosen] = useState<string | undefined>()
  // The first line until one is chosen, and again if the chosen one is gone.
  const selected = rows.find((row) => row.disagreement.id === chosen) ?? rows[0]

  return (
    <>
      <VerdictSummary disagreements={run.disagreements} />

      {run.disagreements.length === 0 && (
        <p className="text-tP text-text-strong">
          A revisão leu a receita de novo e não encontrou nada errado na leitura, no pareamento nem
          nos apontamentos.
        </p>
      )}

      {run.rejected > 0 && (
        // Said, not hidden: a review that pointed at things that are not
        // there is a review to trust a little less.
        <p className="text-tSmall text-text-weak">
          {run.rejected === 1
            ? '1 discordância foi descartada por apontar para algo que não existe nesta conferência.'
            : `${String(run.rejected)} discordâncias foram descartadas por apontar para algo que não existe nesta conferência.`}
        </p>
      )}

      {rows.length > 0 && (
        /*
         * One board in the conferência's own dress -- its border, its header,
         * its two columns of receita and sistema -- with the columns a result
         * needs on either side: which entry the line is from, what the review
         * says is wrong with it, and what was answered about the review.
         *
         * Every row shares one grid, so the columns line up down the table
         * whatever each row holds, and every row is one line tall.
         */
        <ComparisonBoard status="no_issues" fillRows role="listbox" aria-label="Discordâncias">
          <div className={`${RESULT_GRID} text-tSmall text-text-weak tracking-[0.04em] uppercase`}>
            <span className="py-1">Item</span>
            <span className="py-1">Receita</span>
            <span className="py-1">No sistema</span>
            <span className="py-1">Parecer da revisão</span>
            <span className="py-1">Nossa avaliação</span>
          </div>
          {rows.map((row) => {
            const id = row.disagreement.id
            const on = row === selected
            const answer = run.assessments[id]?.assessment
            const item = row.what === ABSENT_LINE ? row.where : `${row.what} · ${row.where}`
            return (
              <div
                key={id}
                role="option"
                tabIndex={0}
                aria-selected={on}
                data-result-row={id}
                className={`${RESULT_GRID} cursor-pointer border-t border-t-[rgba(0,13,77,0.06)] ${FOCUS_RING_ROW} ${
                  on ? 'bg-fill-brand-weak' : 'hover:bg-fill-weak'
                }`}
                onClick={() => setChosen(id)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  setChosen(id)
                }}
              >
                <span title={item} className="text-tSmall text-text-weak truncate">
                  {item}
                </span>
                <Side value={row.prescribed} flagged={row.status === 'error'} />
                <Side value={row.quoted} flagged={row.status === 'error'} />
                <span className="text-tSmall text-text-strong truncate">
                  {VERDICT_LABEL[row.disagreement.verdict]}
                </span>
                <span>
                  {answer === undefined ? (
                    <span className="text-tSmall text-text-weak">—</span>
                  ) : (
                    <Tag tone={answer === ReviewAssessment.Right ? 'success' : 'danger'}>
                      {ASSESSMENT_LABEL[answer]}
                    </Tag>
                  )}
                </span>
              </div>
            )
          })}
        </ComparisonBoard>
      )}

      {selected !== undefined && (
        <section aria-label="Análise" data-result-analysis={selected.disagreement.id}>
          <DisagreementNote
            disagreement={selected.disagreement}
            assessment={run.assessments[selected.disagreement.id]}
            onAssess={(assessment) => onAssess(selected.disagreement.id, assessment)}
            onJump={
              selected.stepIndex === undefined
                ? undefined
                : () => onJump(selected.stepIndex!, selected.disagreement)
            }
          />
        </section>
      )}
    </>
  )
}

/**
 * The result table's columns, on the header and on every row alike: item,
 * receita, sistema, problem, answer. Fixed on the right so a long name never
 * pushes the last two out of line.
 */
const RESULT_GRID =
  'grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_11rem_8rem] items-center gap-x-3 px-3 py-1'

/** One side of a line, as the conferência writes it: the value, or a dash. */
function Side({ value, flagged }: { value: string; flagged: boolean }) {
  if (value === '') {
    return (
      <span aria-label="ausente" className="text-tP text-text-weak min-w-0 italic">
        —
      </span>
    )
  }
  return (
    <span
      title={value}
      className={`text-tP min-w-0 truncate ${
        flagged ? 'text-text-error font-semibold' : 'text-text-strong'
      }`}
    >
      {value}
    </span>
  )
}

/** How many of each kind, in the order the result is read. */
function VerdictSummary({ disagreements }: { disagreements: readonly ReviewDisagreement[] }) {
  if (disagreements.length === 0) return null
  return (
    <ul className="text-tSmall text-text-weak flex flex-wrap gap-x-4 gap-y-1">
      {VERDICT_ORDER.map((verdict) => {
        const count = disagreements.filter((one) => one.verdict === verdict).length
        return count === 0 ? null : (
          <li key={verdict}>
            {VERDICT_LABEL[verdict]}: <span className="text-text-strong">{count}</span>
          </li>
        )
      })}
    </ul>
  )
}
